import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsMobile } from 'hooks/useIsMobile';
import { usePrefersReducedMotion } from 'hooks/usePrefersReducedMotion';
import { buildTradeMapGeometry } from './geometry';
import { TradeMapModel } from './model';
import TradeMapHeader, { TradeMapOwner } from './TradeMapHeader';
import TradeMapLegend from './TradeMapLegend';
import TradeMapSvg from './TradeMapSvg';
import TradeMapTooltip from './TradeMapTooltip';
import TradeStatsRow from './TradeStatsRow';
import { TradeMapFilter } from './types';
import './TradeMapBlock.css';

// The map above the Trade History grid with the stat tiles under it (decision
// 5, addendum). Owns the measured size, the variant, the animation pause, hover
// and the tooltip. The share card draws the same geometry on a canvas
// (TradeMapCardCanvas): one model, one geometry pass, two painters.

export const DESKTOP_MAX_HEIGHT = 440;
export const MOBILE_HEIGHT = 200;

interface Props {
  model: TradeMapModel;
  filter: TradeMapFilter;
  /** The profile owner: name, avatar and Elite treatment for the header */
  owner: TradeMapOwner;
  /** The viewer is the owner: the label reads "My trade map" */
  isOwnProfile: boolean;
}

const TradeMapBlock: React.FC<Props> = ({ model, filter, owner, isOwnProfile }) => {
  const ownerName = owner.username;
  const frameRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState(0);
  const [offScreen, setOffScreen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const isMobile = useIsMobile();
  const reducedMotion = usePrefersReducedMotion();
  const [coarse] = useState(() => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  const variant = isMobile ? 'mobile' : 'desktop';
  // Desktop: 2:1 up to the cap, floored at the phone height. The profile's
  // Trade History column is narrow while the desktop filter rail is open (the
  // page grid pushes the column to about 317px at 1280), and a 2:1 map in that
  // column would be 134px tall; the floor keeps it legible at any column width.
  const height = isMobile ? MOBILE_HEIGHT : Math.max(MOBILE_HEIGHT, Math.min(DESKTOP_MAX_HEIGHT, Math.round(width / 2)));

  // Fit to the MEASURED box, coalesced by rAF; sub-pixel deltas are ignored so a
  // scrollbar flicker never rebuilds the geometry
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const next = Math.round(el.clientWidth);
        setWidth((prev) => (Math.abs(prev - next) < 1 ? prev : next));
      });
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(frame);
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, []);

  // Off-screen or tab hidden: CSS animations pause by class; SMIL needs the API
  useEffect(() => {
    const el = frameRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setOffScreen(!entry.isIntersecting));
    observer.observe(el);
    const onVisibility = () => setHidden(document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);
  const paused = offScreen || hidden;
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg || typeof svg.pauseAnimations !== 'function') return;
    if (paused) svg.pauseAnimations();
    else svg.unpauseAnimations();
  }, [paused]);

  const geometry = useMemo(() => (width > 0 ? buildTradeMapGeometry(model, variant, width, height) : null), [model, variant, width, height]);
  const interactive = !isMobile && !coarse;
  const onHover = useCallback((key: string | null) => setHoveredKey(key), []);
  const hoveredDot = hoveredKey && geometry ? geometry.dots.find((d) => d.key === hoveredKey) ?? null : null;

  const { totals } = model;
  const ariaLabel = `${ownerName}'s trade map: ${totals.received.toLocaleString('en-US')} received, ${totals.sent.toLocaleString('en-US')} sent, ${totals.partners.toLocaleString('en-US')} partners in ${totals.countries.toLocaleString('en-US')} countries`;

  return (
    <section
      className="ptm-block"
      data-testid="profile-trade-map"
      data-variant={variant}
      data-filter={filter}
      data-reduced-motion={reducedMotion}
      aria-label={`${ownerName}'s trade map`}
      // Read back by the verification evidence: what was drawn, from the geometry
      data-geometry={
        geometry
          ? JSON.stringify({
              width: geometry.width,
              height: geometry.height,
              lonSpanPx: Number(geometry.lonSpanPx.toFixed(2)),
              scale: Number(geometry.scale.toFixed(2)),
              home: geometry.home,
              traded: geometry.countries.filter((c) => c.traded).map((c) => c.id),
              dots: geometry.dots.map((d) => ({ key: d.key, x: Number(d.x.toFixed(1)), y: Number(d.y.toFixed(1)), r: Number(d.r.toFixed(2)) })),
              arcs: geometry.arcs.map((a) => ({ key: a.key, direction: a.direction, count: a.count, live: a.live, width: a.width, mid: { x: Number(a.mid.x.toFixed(1)), y: Number(a.mid.y.toFixed(1)) } })),
            })
          : undefined
      }
    >
      <div className="ptm-map-frame" ref={frameRef} style={{ height }} onMouseLeave={interactive ? () => setHoveredKey(null) : undefined}>
        {geometry && (
          <TradeMapSvg
            ref={svgRef}
            geometry={geometry}
            filter={filter}
            static={reducedMotion}
            paused={paused}
            interactive={interactive}
            hoveredKey={hoveredKey}
            onHover={onHover}
            ariaLabel={ariaLabel}
          />
        )}
        <TradeMapHeader owner={owner} isOwnProfile={isOwnProfile} countries={totals.countries} inTransit={totals.inTransit} compact={isMobile} />
        {isMobile && <TradeMapLegend inTransit={totals.inTransit} compact />}
        {interactive && hoveredDot && geometry && <TradeMapTooltip dot={hoveredDot} model={model} width={geometry.width} />}
      </div>
      <TradeStatsRow model={model} />
    </section>
  );
};

export default TradeMapBlock;
